import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormLabel,
  FormControl,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Description,
  LocationOn,
  Business,
  Assignment,
  Download,
  Assessment,
  Clear,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { useActivityLog } from '../context/ActivityLogContext';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const SalesOrderCreation = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  // State management
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedOrderType, setSelectedOrderType] = useState('PEA2622');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [processingLocation, setProcessingLocation] = useState(null);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);

  // Refs for async processing
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const activityProcessIdRef = useRef(null);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (activityProcessIdRef.current) {
          clearIntervalRef(activityProcessIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const orderTypes = [
    { id: 'PEA2622', label: 'PEA2622 (MINE Template)' },
    { id: 'NEB6100', label: 'NEB6100' },
    { id: '99STG2', label: '99STG2 (99ONE1 Template)' },
    { id: 'SEL6355', label: 'SEL6355' },
    { id: 'Metropolitan', label: 'Metropolitan' },
  ];

  const locationButtons = [
    { id: 'California', label: 'California' },
    { id: 'GeorgiaPortWentworth', label: 'Georgia Port Wentworth' },
    { id: 'GeorgiaMidway', label: 'Georgia Midway' },
    { id: 'Indianapolis', label: 'Indianapolis' },
    { id: 'NJCranbury', label: 'NJ Cranbury' },
    { id: 'PACarlisle', label: 'PA Carlisle' },
    { id: 'PAEaston', label: 'PA Easton' },
    { id: 'Texas', label: 'Texas' },
  ];

  const specialProcessingButtons = [
    { id: 'COL8400', label: 'COL8400 Orders', color: TOOL_COLORS.LINK.primary, hoverColor: TOOL_COLORS.LINK.hover },
    { id: 'TitanCargo', label: 'Titan Cargo Orders', color: TOOL_COLORS.EXCEL.primary, hoverColor: TOOL_COLORS.EXCEL.hover },
  ];

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      validateAndSetFiles(files);
    }
  };

  // Validate files
  const validateAndSetFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        errors.push(`${file.name} is not a valid PDF file`);
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        errors.push(`${file.name} exceeds 100MB limit`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    setSelectedFiles(validFiles);
    setError('');
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      validateAndSetFiles(droppedFiles);
    }
  };

  // Fetch status from server and update activity log
  const fetchStatus = async (currentProcessId, activityProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.SALES_ORDER_STATUS(currentProcessId), {
        method: 'GET',
      });

      // Update activity log with server logs
      if (response.logs && response.logs.length > 0) {
        setLogs(activityProcessId, response.logs);
      }

      // Update progress
      if (response.progressPercentage !== undefined) {
        updateActivityProcess(activityProcessId, { progressPercentage: response.progressPercentage });
        setProgress(response.progressPercentage);
      }

      // Check if completed or errored
      if (response.status === 'completed') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        // Auto-download if not already triggered
        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(currentProcessId, activityProcessId, response.fileName);
        }

        setSuccess('Sales order processed successfully! Excel file downloaded.');
        setResults({ filesProcessed: response.processedItems || selectedFiles.length, orderType: selectedOrderType });
        completeActivityProcess(activityProcessId, true);
        enqueueSnackbar('Sales order processed and downloaded successfully!', { variant: 'success' });
        setIsProcessing(false);
        setProgress(0);
      } else if (response.status === 'error') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        setError(response.message || 'Processing failed. Please check the activity log for details.');
        completeActivityProcess(activityProcessId, false);
        enqueueSnackbar('An error occurred while processing the sales order', { variant: 'error' });
        setIsProcessing(false);
        setProgress(0);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download file from server
  const downloadFile = async (currentProcessId, activityProcessId, fileName) => {
    try {
      const response = await fetch(API_ENDPOINTS.SALES_ORDER_DOWNLOAD(currentProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `Order_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(activityProcessId, `✓ Excel downloaded: ${fileName}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(activityProcessId, `✗ Error downloading file: ${err.message}`);
    }
  };

  // Process files with selected template
  const handleProcessFiles = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select PDF files first');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    // Generate a unique process ID for activity log
    const activityProcessId = `sales-order-${Date.now()}`;
    activityProcessIdRef.current = activityProcessId;

    // Add to global activity log
    const orderTypeLabel = orderTypes.find(t => t.id === selectedOrderType)?.label || selectedOrderType;
    addActivityProcess(activityProcessId, `Sales Order (${orderTypeLabel})`, 'Sales Order Creation');
    addLog(activityProcessId, `Starting sales order processing...`);
    addLog(activityProcessId, `Order Type: ${orderTypeLabel}`);
    addLog(activityProcessId, `Files: ${selectedFiles.length}`);
    selectedFiles.forEach(file => {
      addLog(activityProcessId, `  • ${file.name}`);
    });

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append(`files`, file);
      });
      formData.append('orderType', selectedOrderType);

      addLog(activityProcessId, `⏳ Uploading files to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 10 });
      setProgress(10);

      // Make API call to start processing
      const response = await apiCall(API_ENDPOINTS.SALES_ORDER_PROCESS, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addLog(activityProcessId, `⏳ Processing started on server...`);

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus(serverProcessId, activityProcessId);
        }, 1000);
        setIntervalRef(activityProcessId, intervalRef.current);
      } else {
        throw new Error('No process ID returned from server');
      }

    } catch (error) {
      console.error('Processing error:', error);
      addLog(activityProcessId, `✗ Error: ${error.message}`);
      completeActivityProcess(activityProcessId, false);
      setError('An error occurred while processing the sales order');
      enqueueSnackbar('An error occurred while processing the sales order', { variant: 'error' });
      setIsProcessing(false);
      setProgress(0);
    }
  };

  // Process location-based automated processing
  const handleLocationProcessing = async (locationId) => {
    setProcessingLocation(locationId);
    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiCall(API_ENDPOINTS.SALES_ORDER_LOCATION(locationId), {
        method: 'POST',
        body: JSON.stringify({ orderType: '99STG2' })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${locationId}_Order_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`${locationId} orders processed successfully!`);
      enqueueSnackbar(`${locationId} orders processed successfully!`, { variant: 'success' });
    } catch (error) {
      setError(`An error occurred while processing ${locationId} orders`);
      enqueueSnackbar(`An error occurred while processing ${locationId} orders`, { variant: 'error' });
    } finally {
      setIsProcessing(false);
      setProcessingLocation(null);
    }
  };

  // Process special orders (COL8400, TitanCargo) - sends selected files
  const handleSpecialProcessing = async (specialType) => {
    if (selectedFiles.length === 0) {
      setError('Please select PDF files first to process special orders');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(API_ENDPOINTS.SALES_ORDER_SPECIAL(specialType), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Processing failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${specialType}_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`${specialType} orders processed successfully!`);
      enqueueSnackbar(`${specialType} orders processed successfully!`, { variant: 'success' });
    } catch (error) {
      setError(error.message || `An error occurred while processing ${specialType} orders`);
      enqueueSnackbar(`An error occurred while processing ${specialType} orders`, { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedFiles([]);
    setSelectedOrderType('PEA2622');
    setError('');
    setSuccess('');
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Sales Order Creation" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          SALES ORDER CREATION
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Order Configuration
              </Typography>

              {/* Order Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Select Template Type
                    </FormLabel>
                    <RadioGroup
                      value={selectedOrderType}
                      onChange={(e) => setSelectedOrderType(e.target.value)}
                      sx={{ mt: 1 }}
                    >
                      {orderTypes.map((type) => (
                        <FormControlLabel
                          key={type.id}
                          value={type.id}
                          control={<Radio />}
                          label={type.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: selectedFiles.length > 0 ? getCardBackground(theme) : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Import Files (PDF)
                    </Typography>
                    {selectedFiles.length > 0 && (
                      <Tooltip title="Files selected">
                        <CheckCircle color="success" />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Drag and Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.EXCEL.primary : selectedFiles.length > 0 ? theme.palette.success.main : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging ? TOOL_COLORS.EXCEL.light : selectedFiles.length > 0 ? `${theme.palette.success.main}10` : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: selectedFiles.length > 0 ? theme.palette.success.main : TOOL_COLORS.EXCEL.primary,
                        bgcolor: selectedFiles.length > 0 ? `${theme.palette.success.main}15` : TOOL_COLORS.EXCEL.light,
                      },
                    }}
                  >
                    {selectedFiles.length > 0 ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: theme.palette.success.main, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {selectedFiles.length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop more files to add
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.EXCEL.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop files here' : 'Drag & drop PDFs here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF files only, max 100MB per file
                        </Typography>
                      </>
                    )}
                  </Box>

                  {selectedFiles.length > 0 && (
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {selectedFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1,
                            p: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.EXCEL.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => { setSelectedFiles(prev => prev.filter((_, i) => i !== index)); }} sx={{ color: theme.palette.error.main }}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUpload />}
                      sx={{ borderColor: TOOL_COLORS.EXCEL.primary, color: TOOL_COLORS.EXCEL.primary }}
                    >
                      Select PDF Files
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".pdf"
                        multiple
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessFiles}
                  disabled={selectedFiles.length === 0 || isProcessing}
                  sx={{
                    bgcolor: TOOL_COLORS.EXCEL.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.EXCEL.hover },
                    px: 4
                  }}
                >
                  {isProcessing && !processingLocation ? 'Creating Order...' : 'Create Order'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ borderColor: theme.palette.divider }}
                >
                  Reset
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {isProcessing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {processingLocation ? `Processing ${processingLocation}...` : 'Processing order...'} {progress > 0 ? `${Math.round(progress)}%` : ''}
                  </Typography>
                  <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} color="primary" />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Assessment color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.filesProcessed} Files Processed`}
                        secondary="Converted to Excel format"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Download color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Order Summary"
                        secondary="Excel file auto-downloaded"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Order summary has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Select template type<br />
                2. Upload PDF file(s)<br />
                3. Click "Create Order"<br />
                4. Download Excel summary<br /><br />
                <strong>Note:</strong> Automated location processing available in quick actions below
              </Typography>
            </Paper>

            {/* Quick Actions */}
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Quick Actions
              </Typography>

              {/* Location Processing */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                STG Locations (99STG2):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {locationButtons.map((location) => (
                  <Button
                    key={location.id}
                    variant="outlined"
                    size="small"
                    startIcon={<LocationOn />}
                    onClick={() => handleLocationProcessing(location.id)}
                    disabled={isProcessing}
                    sx={{
                      borderColor: theme.palette.divider,
                      justifyContent: 'flex-start',
                      '&:hover': {
                        borderColor: TOOL_COLORS.EXCEL.primary,
                        bgcolor: TOOL_COLORS.EXCEL.light,
                      }
                    }}
                  >
                    {location.label}
                  </Button>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Special Processing */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Special Orders:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {specialProcessingButtons.map((special) => (
                  <Button
                    key={special.id}
                    variant="contained"
                    size="small"
                    startIcon={special.id === 'COL8400' ? <Business /> : <Assignment />}
                    onClick={() => handleSpecialProcessing(special.id)}
                    disabled={isProcessing || selectedFiles.length === 0}
                    sx={{
                      bgcolor: special.color,
                      '&:hover': {
                        bgcolor: special.hoverColor || special.color,
                      }
                    }}
                  >
                    {special.label}
                  </Button>
                ))}
                {selectedFiles.length === 0 && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                    Select PDF files above to enable special order processing
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SalesOrderCreation;
